import React from "react";
import {
  Table,
  Tag,
  Space,
  Switch,
  Popconfirm,
  Tooltip,
  Typography,
} from "antd";
import {
  DeleteOutlined,
  EditOutlined,
  FolderAddOutlined,
  KeyOutlined,
} from "@ant-design/icons";
const { Text } = Typography;

export const tagStyles = {
  padding: "2px 8px",
  borderRadius: 4,
  fontSize: 12,
  fontWeight: 500,
  marginRight: 4,
  fontFamily: "Inter",
};

const statusTag = (active) => ({
  backgroundColor: active ? "#f6ffed" : "#fff1f0",
  color: active ? "#389e0d" : "#cf1322",
  ...tagStyles,
});

export default function ApplicationsTable({
  applications,
  loading,
  onEdit,
  onDelete,
  onToggleActive,
  onManageSecrets,
  pagination,
  onChange,
}) {
  const columns = [
    {
      title: "Name",
      dataIndex: "name",
      key: "name",
      ellipsis: { showTitle: true },
      render: (text) => <span style={{ fontSize: 13 }}>{text}</span>,
    },
    {
      title: "Client ID",
      dataIndex: "clientId",
      key: "clientId",
      ellipsis: true,
      render: (id) => (
        <Tooltip title={id}>
          <span style={{ fontSize: 12, fontFamily: "monospace" }}>
            {id?.slice(0, 18)}...
          </span>
        </Tooltip>
      ),
    },
    {
      title: "Secrets",
      key: "secrets",
      render: (_, record) => (
        <div style={{ display: "flex", gap: 4, flexWrap: "wrap" }}>
          {record.hasPrimarySecret ? (
            <Tag
              style={{ ...tagStyles, background: "#e6f7ff", color: "#096dd9" }}
            >
              Primary ✓
            </Tag>
          ) : (
            <Tag
              style={{ ...tagStyles, background: "#fff1f0", color: "#cf1322" }}
            >
              No Primary
            </Tag>
          )}

          {record.hasSecondarySecret ? (
            <Tag
              style={{ ...tagStyles, background: "#f6ffed", color: "#389e0d" }}
            >
              Secondary ✓
            </Tag>
          ) : (
            <Tag
              style={{ ...tagStyles, background: "#fff1f0", color: "#cf1322" }}
            >
              No Secondary
            </Tag>
          )}
        </div>
      ),
    },
    {
      title: "Status",
      key: "status",
      render: (_, record) => (
        <Tag style={statusTag(record.active)}>
          {record.active ? "Active" : "Inactive"}
        </Tag>
      ),
    },
    {
      title: "Actions",
      key: "actions",
      render: (_, record) => (
        <Space>
          <Tooltip title="Edit" style={{ fontFamily: "Inter", fontSize: 10 }}>
            <EditOutlined
              style={{ cursor: "pointer", marginRight: 10 }}
              onClick={() => onEdit(record)}
            />
          </Tooltip>

          <Popconfirm
            title="Are you sure?"
            onConfirm={() => onDelete(record.id)}
          >
            <DeleteOutlined
              style={{ cursor: "pointer", color: "red", marginRight: 10 }}
            />
          </Popconfirm>

          <Tooltip
            title="Manage Secrets"
            style={{ fontFamily: "Inter", fontSize: 10 }}
          >
            <FolderAddOutlined
              style={{ cursor: "pointer" }}
              onClick={() => onManageSecrets(record)}
            />
          </Tooltip>
        </Space>
      ),
    },
  ];

  return (
    <Table
      columns={columns}
      dataSource={applications}
      loading={loading}
      rowKey="id"
      size="small"
      pagination={pagination}
      onChange={onChange}
      expandable={{
        expandedRowRender: (record) => (
          <div style={{ padding: 8 }}>
            <p className="mb-1 pb-0">
              <strong>Code:</strong> {record.code || "-"}
            </p>
            <p className="mb-1 pb-0">
              <strong>Description:</strong>{" "}
              {record.description || (
                <span style={{ color: "#888" }}>No description</span>
              )}
            </p>

            <p className="mb-1 pb-0">
              <strong>Client ID:</strong>{" "}
              <Text
                copyable={{
                  text: record.clientId,
                  tooltips: ["Copy Client ID", "Copied!"],
                }}
                ellipsis={{ tooltip: record.clientId }}
                style={{ fontSize: 12 }}
              >
                {record.clientId}
              </Text>
            </p>

            <p className="mb-1 pb-0">
              <strong>Primary Secret:</strong>{" "}
              {record.hasPrimarySecret
                ? `****${record.primarySecretLast4 || ""}`
                : "Not created"}
            </p>

            <p className="mb-1 pb-0">
              <strong>Secondary Secret:</strong>{" "}
              {record.hasSecondarySecret
                ? `****${record.secondarySecretLast4 || ""}`
                : "Not created"}
            </p>

            <p className="mb-1 pb-0">
              <strong>Created At:</strong>{" "}
              {record.createdAt || <span style={{ color: "#888" }}>N/A</span>}
            </p>

            <p className="mb-1 pb-0">
              <strong>Updated At:</strong>{" "}
              {record.updatedAt || <span style={{ color: "#888" }}>N/A</span>}
            </p>
          </div>
        ),
        rowExpandable: (record) =>
          record.description ||
          record.clientId ||
          record.hasPrimarySecret ||
          record.hasSecondarySecret,
      }}
      className="custom-table"
    />
  );
}
